import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiDownload, FiPrinter, FiShare2, FiFileText } from 'react-icons/fi';
import html2pdf from 'html2pdf.js';
import toast from 'react-hot-toast';
import InvoiceTemplate from '../components/InvoiceTemplate';
import useSaleStore from '../../store/saleStore';
import api from '../../services/api';
import './InvoicePreview.css';

const InvoicePreview = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const invoiceRef = useRef();
  const { sale, isLoading, fetchSale } = useSaleStore();
  const [settings, setSettings] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [creditNotes, setCreditNotes] = useState([]);
  const [generatingCN, setGeneratingCN] = useState(false);

  useEffect(() => {
    if (id) {
      fetchSale(id);
      // Fetch company settings
      api.get('/settings').then(res => {
        setSettings(res.data.data);
      }).catch(err => {
        console.error('Failed to fetch settings:', err);
      });
      // Fetch credit notes
      api.get(`/credit-notes?originalInvoice=${id}`).then(res => {
        setCreditNotes(res.data.data);
      }).catch(err => {
        console.error('Failed to fetch credit notes:', err);
      });
    }
  }, [id, fetchSale]);

  const getFileName = () => {
    const customerCode = sale?.customer?.code || sale?.customerName?.replace(/\s+/g, '-') || 'Customer';
    const invoiceNum = sale?.invoiceNumber?.replace(/\//g, '-') || 'Invoice';
    return `${invoiceNum}-${customerCode}-${Date.now()}.pdf`;
  };

  const generatePDF = async (action = 'download') => {
    if (!invoiceRef.current) return;
    setDownloading(true);

    const el = invoiceRef.current;
    // Force a fixed pixel width for consistent PDF rendering
    const originalWidth = el.style.width;
    const originalFontSmoothing = el.style.webkitFontSmoothing;
    el.style.width = '210mm';
    el.style.webkitFontSmoothing = 'antialiased';

    // Add pdf mode class temporarily to apply specific pdf CSS rules
    el.classList.add('pdf-mode');

    const opt = {
      margin: 0,
      filename: getFileName(),
      image: { type: 'jpeg', quality: 1.0 },
      html2canvas: {
        scale: 2,
        useCORS: true,
        letterRendering: true,
        logging: false,
        backgroundColor: '#FEFEFEfff'
      },
      jsPDF: {
        unit: 'mm',
        format: 'a4',
        orientation: 'portrait'
      },
      pagebreak: { mode: ['avoid-all', 'css', 'legacy'] }
    };

    try {
      if (action === 'download') {
        await html2pdf().set(opt).from(invoiceRef.current).save();
        toast.success('Invoice downloaded successfully!');
      } else if (action === 'print') {
        const pdfBlob = await html2pdf().set(opt).from(invoiceRef.current).output('blob');
        const blobUrl = URL.createObjectURL(pdfBlob);
        const printWindow = window.open(blobUrl, '_blank');
        if (printWindow) {
          printWindow.addEventListener('load', () => {
            printWindow.print();
          });
        }
      } else if (action === 'share') {
        const pdfBlob = await html2pdf().set(opt).from(invoiceRef.current).output('blob');
        const file = new File([pdfBlob], getFileName(), { type: 'application/pdf' });

        if (navigator.share && navigator.canShare({ files: [file] })) {
          await navigator.share({
            title: `Invoice ${sale?.invoiceNumber}`,
            text: `Invoice from ${settings?.companyName || 'Medineo Enterprises'}`,
            files: [file]
          });
          toast.success('Invoice shared!');
        } else {
          // Fallback: download if sharing not supported
          const url = URL.createObjectURL(pdfBlob);
          const a = document.createElement('a');
          a.href = url;
          a.download = getFileName();
          a.click();
          URL.revokeObjectURL(url);
          toast.success('Invoice downloaded (sharing not supported on this device)');
        }
      }
    } catch (error) {
      console.error('PDF generation failed:', error);
      toast.error('Failed to generate PDF');
    } finally {
      el.classList.remove('pdf-mode');
      el.style.width = originalWidth;
      el.style.webkitFontSmoothing = originalFontSmoothing;
      setDownloading(false);
    }
  };

  const handleGenerateCreditNote = async () => {
    if (!sale) return;
    try {
      setGeneratingCN(true);
      const res = await api.post('/credit-notes', { originalInvoiceId: sale._id });
      if (res.data.success) {
        toast.success('Credit Note generated successfully!');
        navigate(`/erp/credit-notes/${res.data.data._id}/preview`);
      }
    } catch (error) {
      console.error('Error generating credit note:', error);
      toast.error(error.response?.data?.message || 'Failed to generate Credit Note');
    } finally {
      setGeneratingCN(false);
    }
  };

  if (isLoading || !sale) {
    return (
      <div className="invoice-preview-page">
        <div className="invoice-loading">
          <div className="loader"></div>
          <p>Loading invoice...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="invoice-preview-page">
      {/* Toolbar */}
      <div className="invoice-toolbar">
        <div className="toolbar-left">
          <button className="btn btn-ghost" onClick={() => navigate(-1)}>
            <FiArrowLeft /> Back
          </button>
          <div className="toolbar-info">
            <h2>Invoice {sale?.invoiceNumber}</h2>
            <span className="toolbar-customer">{sale?.customerName}</span>
            {creditNotes.length > 0 && (
              <span className="badge badge-warning" style={{ marginLeft: '10px', fontSize: '0.8rem', padding: '2px 8px', borderRadius: '12px', background: '#fef08a', color: '#854d0e', border: '1px solid #fde047' }}>
                Credit Note Exists
              </span>
            )}
          </div>
        </div>
        <div className="toolbar-actions">
          {creditNotes.map(cn => (
            <button
              key={cn._id}
              className="btn btn-outline"
              onClick={() => navigate(`/erp/credit-notes/${cn._id}/preview`)}
            >
              <FiFileText /> View {cn.creditNoteNumber}
            </button>
          ))}
          <button
            className="btn btn-outline"
            onClick={handleGenerateCreditNote}
            disabled={generatingCN}
            style={{ borderColor: '#ef4444', color: '#ef4444' }}
          >
            <FiFileText /> {generatingCN ? 'Generating...' : 'Generate Credit Note'}
          </button>
          <button
            className="btn btn-outline"
            onClick={() => generatePDF('print')}
            disabled={downloading}
          >
            <FiPrinter /> Print
          </button>
          <button
            className="btn btn-outline"
            onClick={() => generatePDF('share')}
            disabled={downloading}
          >
            <FiShare2 /> Share
          </button>
          <button
            className="btn btn-primary"
            onClick={() => generatePDF('download')}
            disabled={downloading}
          >
            <FiDownload /> {downloading ? 'Generating...' : 'Download PDF'}
          </button>
        </div>
      </div>

      {/* Invoice Preview */}
      <div className="invoice-preview-container">
        <div className="invoice-paper">
          <InvoiceTemplate ref={invoiceRef} sale={sale} settings={settings} />
        </div>
      </div>
    </div>
  );
};

export default InvoicePreview;
